/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { ISchemaActionRefModel, SCHEMA_VALUE_TYPE, TGenericModelNode } from "@adapptio/lib-blueprint";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { InspectModel } from "../InspectModel";
import { FieldRow } from "../../Fields/FieldRow";
import { FieldNote } from "../../Fields/FieldNote";
import { Icon, ICON_SIZE } from "../../Icon";

export const ControlActionRef_PrimaryChildren: TInspectorControlComponent<ISchemaActionRefModel> = ({
	model: _model,
	path,
	depth
}) => {
	const { model } = useBlueprintModel(_model, "actionRef");
	const { model: actionIdModel } = useBlueprintModel(_model?.actionId);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<InspectModel model={actionIdModel} path={path.concat([ "actionId" ])} depth={depth} />
		</>
	);
};

export const ControlActionRef_Children: TInspectorControlComponent<ISchemaActionRefModel> = ({
	model: _model,
	path,
	depth
}) => {
	const { model } = useBlueprintModel(_model, "actionRef");
	const { model: actionIdModel } = useBlueprintModel(_model?.actionId);
	const { model: paramsModel } = useBlueprintModel(_model?.params);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			{paramsModel?.value ? (
				<InspectModel model={paramsModel.value} path={path.concat([ "params" ])} depth={depth} />
			) : actionIdModel.type === SCHEMA_VALUE_TYPE.CONST && actionIdModel.constant.value ? (
				<FieldRow>
					<FieldNote color="warning" padded>
						<Icon name="mdi/alert" size={ICON_SIZE.XSMALL} />
						{/* @todo Translation */}
						<span>Action not found.</span>
					</FieldNote>
				</FieldRow>
			) : null}
		</>
	);
};

export const ControlActionRef = (model: TGenericModelNode): IInspectorControls => ({
	primaryChildren: {
		component: ControlActionRef_PrimaryChildren,
		model: model
	},
	children: {
		component: ControlActionRef_Children,
		model: model
	}
	// displayInSection: true
});
